import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { devtools } from 'zustand/middleware';
import { createWithEqualityFn } from 'zustand/traditional';
import { shallow } from 'zustand/shallow';
import {
  CandidateRow,
  CreateImportResult,
  ProjectElementImport,
  ResourceCodeMapping,
  ResourceCodeMappingUpsertItem,
} from '@shared/models/element-import.model';
import { elementImportsApi } from './element-imports.api';
import { shouldLoadElementImports } from './element-imports-fetch.helpers';

/** mappingsByCode[resourceCode] -> the resource-code mapping row. */
export type MappingsByCode = Record<string, ResourceCodeMapping>;

export type ElementImportsData = {
  importsById: Record<string, ProjectElementImport>;
  rowsByImport: Record<string, CandidateRow[]>;
  mappingsByCode: MappingsByCode;
};

export type ProjectImportsIdle = { status: 'idle' };
export type ProjectImportsFetching = { status: 'fetching' };
export type ProjectImportsError = { status: 'error'; error: Error };
export type ProjectImportsReady = {
  status: 'ready';
  data: ElementImportsData;
  isFetching: boolean;
};

export type ProjectImportsState =
  | ProjectImportsIdle
  | ProjectImportsFetching
  | ProjectImportsError
  | ProjectImportsReady;

const IDLE: ProjectImportsIdle = { status: 'idle' };

const emptyData = (): ElementImportsData => ({
  importsById: {},
  rowsByImport: {},
  mappingsByCode: {},
});

const projectKey = (projectId: number): string => String(projectId);

const put = (
  byKey: Record<string, ProjectImportsState>,
  key: string,
  next: ProjectImportsState,
): Record<string, ProjectImportsState> => ({ ...byKey, [key]: next });

const indexImports = (
  imports: ProjectElementImport[],
): Record<string, ProjectElementImport> =>
  Object.fromEntries(imports.map((i) => [i.id, i]));

const groupRowsByImport = (
  rows: CandidateRow[],
): Record<string, CandidateRow[]> => {
  const out: Record<string, CandidateRow[]> = {};
  for (const row of rows) {
    (out[row.importId] ??= []).push(row);
  }
  return out;
};

const indexMappings = (mappings: ResourceCodeMapping[]): MappingsByCode =>
  Object.fromEntries(mappings.map((m) => [m.resourceCode, m]));

const toData = (
  imports: ProjectElementImport[],
  rows: CandidateRow[],
  mappings: ResourceCodeMapping[],
): ElementImportsData => ({
  importsById: indexImports(imports),
  rowsByImport: groupRowsByImport(rows),
  mappingsByCode: indexMappings(mappings),
});

const mergeUpload = (
  data: ElementImportsData,
  result: CreateImportResult,
): ElementImportsData => ({
  importsById: { ...data.importsById, [result.import.id]: result.import },
  rowsByImport: { ...data.rowsByImport, [result.import.id]: result.rows },
  mappingsByCode: {
    ...data.mappingsByCode,
    ...indexMappings(result.mappings),
  },
});

const readData = (
  state: ProjectImportsState | undefined,
): ElementImportsData => (state?.status === 'ready' ? state.data : emptyData());

const inflightFetches = new Map<string, Promise<ElementImportsData>>();

const fetchDeduped = (projectId: number): Promise<ElementImportsData> => {
  const key = projectKey(projectId);
  const existing = inflightFetches.get(key);
  if (existing) return existing;
  const p = elementImportsApi
    .listByProject(projectId)
    .then(({ imports, rows, mappings }) => toData(imports, rows, mappings))
    .finally(() => inflightFetches.delete(key));
  inflightFetches.set(key, p);
  return p;
};

type ElementImportsStoreState = {
  byKey: Record<string, ProjectImportsState>;

  fetchFor: (projectId: number) => Promise<void>;
  uploadCsv: (projectId: number, file: File) => Promise<CreateImportResult>;
  deleteImport: (projectId: number, importId: string) => Promise<void>;
  upsertMappings: (
    projectId: number,
    mappings: ResourceCodeMappingUpsertItem[],
    scope: MappingSaveScope,
  ) => Promise<void>;
};

/** Save scope for a resource-code mapping: organization-wide or this project only. */
export type MappingSaveScope = 'org' | 'project';

export const useElementImportsStore =
  createWithEqualityFn<ElementImportsStoreState>()(
    devtools(
      (set) => ({
        byKey: {},

        fetchFor: async (projectId) => {
          // Public routes send a shared-project Organization header; list 404s.
          if (!shouldLoadElementImports(projectId, window.location.pathname)) {
            return;
          }
          const key = projectKey(projectId);
          set(({ byKey }) => {
            const cur = byKey[key];
            const next: ProjectImportsState =
              cur?.status === 'ready'
                ? { ...cur, isFetching: true }
                : { status: 'fetching' };
            return { byKey: put(byKey, key, next) };
          });
          try {
            const data = await fetchDeduped(projectId);
            set(({ byKey }) => {
              const cur = byKey[key];
              const next: ProjectImportsReady =
                cur?.status === 'ready'
                  ? { ...cur, data, isFetching: false }
                  : {
                      status: 'ready',
                      data,
                      isFetching: false,
                    };
              return { byKey: put(byKey, key, next) };
            });
          } catch (e) {
            set(({ byKey }) => {
              const cur = byKey[key];
              const error =
                e instanceof Error ? e : new Error('Failed to load imports');
              if (cur?.status === 'ready') {
                return {
                  byKey: put(byKey, key, { ...cur, isFetching: false }),
                };
              }
              return { byKey: put(byKey, key, { status: 'error', error }) };
            });
          }
        },

        uploadCsv: async (projectId, file) => {
          const key = projectKey(projectId);
          const result = await elementImportsApi.upload(projectId, file);
          set(({ byKey }) => {
            const cur = byKey[key];
            const data = mergeUpload(readData(cur), result);
            const next: ProjectImportsReady = {
              status: 'ready',
              data,
              isFetching: false,
            };
            return { byKey: put(byKey, key, next) };
          });
          return result;
        },

        deleteImport: async (projectId, importId) => {
          await elementImportsApi.remove(importId);
          const key = projectKey(projectId);
          set(({ byKey }) => {
            const cur = byKey[key];
            if (cur?.status !== 'ready') return { byKey };
            const nextImports = { ...cur.data.importsById };
            delete nextImports[importId];
            const nextRows = { ...cur.data.rowsByImport };
            delete nextRows[importId];
            return {
              byKey: put(byKey, key, {
                ...cur,
                data: {
                  ...cur.data,
                  importsById: nextImports,
                  rowsByImport: nextRows,
                },
              }),
            };
          });
        },

        upsertMappings: async (projectId, mappings, scope) => {
          const saved = await elementImportsApi.upsertMappings(
            mappings,
            scope === 'project' ? projectId : undefined,
          );
          const key = projectKey(projectId);
          set(({ byKey }) => {
            const cur = byKey[key];
            if (cur?.status !== 'ready') return { byKey };
            const mappingsByCode = { ...cur.data.mappingsByCode };
            for (const mapping of saved) {
              mappingsByCode[mapping.resourceCode] = mapping;
            }
            return {
              byKey: put(byKey, key, {
                ...cur,
                data: { ...cur.data, mappingsByCode },
              }),
            };
          });
        },
      }),
      { name: 'element-imports' },
    ),
    shallow,
  );

/** Imperative read for callbacks that must see the latest project bucket. */
export const getElementImportsData = (
  projectId: number,
): ElementImportsData | undefined => {
  const state = useElementImportsStore.getState().byKey[projectKey(projectId)];
  return state?.status === 'ready' ? state.data : undefined;
};

/**
 * Subscribe to the import slice for a project bucket and auto-fetch when idle.
 */
export const useProjectImportsState = (
  projectId: number,
): ProjectImportsState => {
  const key = projectKey(projectId);
  const { pathname } = useLocation();
  const state = useElementImportsStore((s) => s.byKey[key] ?? IDLE);

  useEffect(() => {
    if (!shouldLoadElementImports(projectId, pathname)) return;
    const cur = useElementImportsStore.getState().byKey[key];
    if (cur && cur.status !== 'idle') return;
    void useElementImportsStore.getState().fetchFor(projectId);
  }, [key, pathname, projectId]);

  return state;
};
